
// src/pages/IDCardGeneratorIT.jsx
// VERSI JAVASCRIPT YANG DIPERBAIKI

"use client"

import { useRef, useState, useEffect } from "react"
import * as htmlToImage from "html-to-image"
import { QRCodeCanvas } from "qrcode.react"
import { jsPDF } from "jspdf"
import { motion, AnimatePresence } from "framer-motion"
import {
  ChevronLeft,
  ChevronRight,
  Download,
  RotateCcw,
  User,
  Layers,
  Palette,
  Link,
  Home,
} from "lucide-react"

// --- DATA & KONFIGURASI ---

const itRoles = [
  {
    group: "Development & Engineering",
    roles: [
      "Software Developer", "Web Developer", "Full Stack Developer", "Front-End Developer", "Back-End Developer",
      "Mobile Application Developer", "Game Developer", "DevOps Engineer", "Software Architect", "Embedded Systems Engineer",
      "Machine Learning Engineer", "AI Engineer", "Blockchain Developer", "IoT Developer", "API Developer",
      "Robotics Engineer", "AR/VR Developer"
    ],
  },
  {
    group: "Infrastructure & Operations",
    roles: [
      "IT Support Specialist", "Network Administrator", "Systems Administrator", "Systems Engineer", "Cloud Architect",
      "Site Reliability Engineer (SRE)", "Virtualization Engineer", "IT Operations Manager", "Network Engineer",
      "Technical Support Engineer", "IT Asset Manager", "Network Operations Center (NOC) Engineer",
    ],
  },
  {
    group: "Data & Analytics",
    roles: [
      "Database Administrator", "Data Scientist", "Data Analyst", "Business Intelligence Analyst", "Data Engineer",
      "Big Data Engineer", "Data Warehouse Specialist",
    ],
  },
  {
    group: "Security",
    roles: [
      "Cybersecurity Analyst", "IT Security Manager", "Cloud Security Engineer", "Network Security Engineer",
      "Penetration Tester (Ethical Hacker)", "IT Risk Analyst", "IT Compliance Analyst", "IT Auditor",
    ],
  },
  {
    group: "Management & Strategy",
    roles: [
      "IT Project Manager", "IT Consultant", "Systems Analyst", "IT Trainer", "UI/UX Designer",
      "ERP Consultant", "Technical Writer", "IT Business Analyst", "IT Change Manager", "IT Governance Specialist",
      "IT Procurement Specialist"
    ],
  },
];


const availableFonts = [
  { name: "Orbitron", value: "'Orbitron', sans-serif", url: "https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&display=swap" },
  { name: "VT323", value: "VT323, monospace", url: "https://fonts.googleapis.com/css2?family=VT323&display=swap" },
  { name: "Source Code Pro", value: "'Source Code Pro', monospace", url: "https://fonts.googleapis.com/css2?family=Source+Code+Pro:wght@400;600;800&display=swap" },
  { name: "Roboto Mono", value: "'Roboto Mono', monospace", url: "https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@400;700&display=swap" },
  { name: "Share Tech Mono", value: "'Share Tech Mono', monospace", url: "https://fonts.googleapis.com/css2?family=Share+Tech+Mono&display=swap" },
  { name: "Turret Road", value: "'Turret Road', sans-serif", url: "https://fonts.googleapis.com/css2?family=Turret+Road:wght@400;700;800&display=swap" },
];

const designTemplates = [
  {
    id: "cyber-neon",
    name: "Cyber Neon",
    backgroundColor: "#0D1117",
    accentColor: "#39FF14", // Neon Green
    textColor: "#E6EDF3",
    font: "'Orbitron', sans-serif",
    glowColor: "rgba(57, 255, 20, 0.5)",
  },
  {
    id: "arc-blue",
    name: "Arc Reactor Blue",
    backgroundColor: "#010409",
    accentColor: "#00E5FF", // Bright Cyan
    textColor: "#C9D1D9",
    font: "'Share Tech Mono', monospace",
    glowColor: "rgba(0, 229, 255, 0.5)",
  },
  {
    id: "hacker-matrix",
    name: "Hacker Matrix",
    backgroundColor: "#000000",
    accentColor: "#00FF41",
    textColor: "#33FF33",
    font: "'VT323', monospace",
    glowColor: "rgba(0, 255, 65, 0.5)",
  },
  {
    id: "blueprint-tech",
    name: "Blueprint Tech",
    backgroundColor: "#1F2A40",
    accentColor: "#FFFFFF",
    textColor: "#A2B2C2",
    font: "'Source Code Pro', monospace",
    glowColor: "rgba(255, 255, 255, 0.3)",
  }
];

const FORM_STEPS = [
  { id: 1, title: "Identity Core", description: "Enter your name, role, and upload a profile avatar.", icon: User },
  { id: 2, title: "Tech Arsenal", description: "Showcase your technology stack and a personal motto.", icon: Layers },
  { id: 3, title: "Visual Interface", description: "Customize your card's appearance.", icon: Palette },
  { id: 4, title: "Export Module", description: "Download your generated ID card.", icon: Download },
];


// --- KOMPONEN UTAMA ---

export default function ITRoleCardGenerator() {
  const cardRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [employeeId, setEmployeeId] = useState("");

  const initialFormState = {
    name: "Alex Doe",
    role: "Full Stack Developer",
    techStack: "TypeScript - React - Node.js - PostgreSQL",
    profilePic: null,
    motto: "Code is poetry. Efficiency is art.",
    useDefaultPic: false,
  };

  const initialDesignState = {
    font: designTemplates[0].font,
    backgroundColor: designTemplates[0].backgroundColor,
    accentColor: designTemplates[0].accentColor,
    textColor: designTemplates[0].textColor,
    glowColor: designTemplates[0].glowColor,
  };

  const [form, setForm] = useState(initialFormState);
  const [design, setDesign] = useState(initialDesignState);
  const [designMode, setDesignMode] = useState("template");

  useEffect(() => {
    const selectedFont = availableFonts.find((f) => f.value === design.font);
    if (selectedFont?.url) {
      const linkId = "google-font-dynamic-link";
      const existingLink = document.getElementById(linkId);
      if (existingLink) {
        document.head.removeChild(existingLink);
      }
      const link = document.createElement("link");
      link.href = selectedFont.url;
      link.rel = "stylesheet";
      link.id = linkId;
      document.head.appendChild(link);
    }
  }, [design.font]);

  useEffect(() => {
    const randomId = `AGENT-${Math.floor(1000 + Math.random() * 9000)}-${form.role.substring(0, 2).toUpperCase()}`;
    setEmployeeId(randomId);
  }, [form.role]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    if (type === 'file') {
      const files = e.target.files;
      if (files && files[0]) {
        setForm({ ...form, profilePic: URL.createObjectURL(files[0]), useDefaultPic: false });
      }
    } else {
      setForm({ ...form, [name]: value });
    }
  };
  
  const handleDesignChange = (e) => {
    const { name, value } = e.target;
    setDesign({ ...design, [name]: value });
  };
  
  const applyTemplate = (template) => {
    setDesign({
      font: template.font,
      backgroundColor: template.backgroundColor,
      accentColor: template.accentColor,
      textColor: template.textColor,
      glowColor: template.glowColor,
    });
  };

  const resetAll = () => {
    setForm(initialFormState);
    setDesign(initialDesignState);
    setCurrentStep(1);
    setDesignMode("template");
  };

  const nextStep = () => {
    if (currentStep < FORM_STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const getFontEmbedCSS = () => {
    const selectedFont = availableFonts.find((f) => f.value === design.font);
    return selectedFont?.url ? `@import url('${selectedFont.url}');` : "";
  };
  
  const downloadCard = async (format) => {
      const element = cardRef.current;
      if (!element) return;
  
      const pixelRatio = 3;
      const fileName = `${form.name.replace(/\s+/g, '_')}_IT_Role_Card`;
  
      try {
          const dataUrl = await htmlToImage.toPng(element, {
              pixelRatio,
              width: element.offsetWidth,
              height: element.offsetHeight,
              fontEmbedCSS: getFontEmbedCSS(),
              backgroundColor: design.backgroundColor,
          });
  
          if (format === 'png') {
              const link = document.createElement("a");
              link.download = `${fileName}.png`;
              link.href = dataUrl;
              link.click();
          } else {
              const pdf = new jsPDF({
                  orientation: "landscape",
                  unit: "px",
                  format: [element.offsetWidth, element.offsetHeight],
              });
              pdf.addImage(dataUrl, "PNG", 0, 0, element.offsetWidth, element.offsetHeight);
              pdf.save(`${fileName}.pdf`);
          }
           alert("Download initiated successfully!");
      } catch (error) {
          console.error("Failed to download card:", error);
          alert("Sorry, the download failed. Please try again or take a screenshot.");
      }
  };

  const renderProfilePic = () => {
    if (!form.useDefaultPic && form.profilePic) {
      return (
        <img
          src={form.profilePic}
          alt="Profile"
          className="w-full h-full object-cover"
        />
      );
    }
    return (
      <div className="w-full h-full flex items-center justify-center bg-black bg-opacity-30">
        <User size={48} style={{ color: design.accentColor }} />
      </div>
    );
  };
  
  const renderStepContent = () => {
    const CurrentStepIcon = FORM_STEPS[currentStep - 1]?.icon || User;
    switch (currentStep) {
      case 1:
        return (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Name / Alias</label>
              <input name="name" value={form.name} onChange={handleChange} className="w-full bg-gray-800 border-gray-600 rounded p-2 focus:ring-2 focus:ring-[var(--accent-color)]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">IT Role</label>
              <select name="role" value={form.role} onChange={handleChange} className="w-full bg-gray-800 border-gray-600 rounded p-2 focus:ring-2 focus:ring-[var(--accent-color)]">
                {itRoles.map(group => (
                  <optgroup key={group.group} label={group.group}>
                    {group.roles.map(role => <option key={role} value={role}>{role}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Profile Avatar</label>
              <input name="profilePic" type="file" accept="image/*" onChange={handleChange} className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[var(--accent-color)] file:text-black hover:file:opacity-80"/>
              <label className="flex items-center mt-2 space-x-2">
                <input type="checkbox" name="useDefaultPic" checked={form.useDefaultPic} onChange={e => setForm({...form, useDefaultPic: e.target.checked})} className="accent-[var(--accent-color)]" />
                <span className="text-sm text-gray-400">Use default avatar</span>
              </label>
            </div>
          </motion.div>
        );
      case 2:
        return (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Primary Tech Stack</label>
              <input name="techStack" value={form.techStack} onChange={handleChange} placeholder="e.g., PHP - HTML - JS" className="w-full bg-gray-800 border-gray-600 rounded p-2 focus:ring-2 focus:ring-[var(--accent-color)]"/>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Motto / Favorite Quote</label>
              <textarea name="motto" value={form.motto} onChange={handleChange} rows={3} className="w-full bg-gray-800 border-gray-600 rounded p-2 focus:ring-2 focus:ring-[var(--accent-color)]"/>
            </div>
          </motion.div>
        );
      case 3:
        return (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="flex space-x-4">
                <button onClick={() => setDesignMode("template")} className={`flex-1 p-2 rounded ${designMode === 'template' ? 'bg-[var(--accent-color)] text-black' : 'bg-gray-700'}`}>Templates</button>
                <button onClick={() => setDesignMode("custom")} className={`flex-1 p-2 rounded ${designMode === 'custom' ? 'bg-[var(--accent-color)] text-black' : 'bg-gray-700'}`}>Custom</button>
            </div>
            <AnimatePresence mode="wait">
            {designMode === 'template' ? (
              <motion.div key="template" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-2 gap-4">
                {designTemplates.map(t => (
                  <div key={t.id} onClick={() => applyTemplate(t)} className="cursor-pointer border-2 rounded p-2" style={{borderColor: t.accentColor}}>
                    <div className="h-12 rounded" style={{backgroundColor: t.backgroundColor, border: `1px solid ${t.textColor}`}}></div>
                    <p className="text-center text-sm mt-2">{t.name}</p>
                  </div>
                ))}
              </motion.div>
            ) : (
              <motion.div key="custom" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                <div><label className="text-sm text-gray-400">Font</label><select name="font" value={design.font} onChange={handleDesignChange} className="w-full bg-gray-800 p-2 rounded" style={{fontFamily: design.font}}>{availableFonts.map(f => <option key={f.name} value={f.value} style={{fontFamily: f.value}}>{f.name}</option>)}</select></div>
                <div className="grid grid-cols-2 gap-4">
                    <div><label className="text-sm text-gray-400">Background</label><input name="backgroundColor" type="color" value={design.backgroundColor} onChange={handleDesignChange} className="w-full h-10 bg-transparent border-0"/></div>
                    <div><label className="text-sm text-gray-400">Accent</label><input name="accentColor" type="color" value={design.accentColor} onChange={handleDesignChange} className="w-full h-10 bg-transparent border-0"/></div>
                    <div><label className="text-sm text-gray-400">Text</label><input name="textColor" type="color" value={design.textColor} onChange={handleDesignChange} className="w-full h-10 bg-transparent border-0"/></div>
                    <div><label className="text-sm text-gray-400">Glow</label><input name="glowColor" type="color" value={design.glowColor} onChange={handleDesignChange} className="w-full h-10 bg-transparent border-0"/></div>
                </div>
              </motion.div>
            )}
            </AnimatePresence>
          </motion.div>
        );
      case 4:
        return (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4 text-center">
            <p className="text-gray-300">Your agent card is ready for deployment.</p>
            <div className="flex gap-4">
            <button onClick={() => downloadCard('png')} className="flex-1 p-3 bg-gray-700 hover:bg-gray-600 rounded-lg flex items-center justify-center gap-2"><Download size={18} /> PNG</button>
            <button onClick={() => downloadCard('pdf')} className="flex-1 p-3 bg-gray-700 hover:bg-gray-600 rounded-lg flex items-center justify-center gap-2"><Download size={18} /> PDF</button>
            </div>
            <p className="text-xs text-gray-500 pt-4">Tip: If download fails, a high-resolution screenshot of the preview is a reliable backup.</p>
          </motion.div>
        );
      default: return null;
    }
  };

  const progress = (currentStep / FORM_STEPS.length) * 100;
  const CurrentStepIcon = FORM_STEPS[currentStep - 1]?.icon || User;
  
  return (
    <div className="min-h-screen bg-[#010409] text-gray-200 font-sans p-4 sm:p-6 lg:p-8" style={{ "--accent-color": design.accentColor, "--glow-color": design.glowColor }}>
      <div className="max-w-7xl mx-auto">
        <header className="flex justify-between items-center mb-6">
          <h1 className="text-2xl md:text-3xl font-bold" style={{fontFamily: "'Orbitron', sans-serif"}}>IT Role ID Card System</h1>
                    <Link to="/" className="inline-flex items-center gap-2 px-4 py-2 bg-black/20 border border-white/10 rounded-full text-sm font-medium text-gray-300 hover:bg-white/10 hover:text-white transition-colors">
            <Home size={16} />
            <span>Kembali ke Home</span>
          </Link>
          <button onClick={resetAll} className="flex items-center gap-2 text-sm px-3 py-2 border rounded-md border-gray-600 hover:bg-gray-800 transition-colors">
            <RotateCcw size={16}/>
            Reset
          </button>
        </header>

        <main className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 bg-gray-900/50 border border-gray-800 rounded-xl p-6 shadow-2xl backdrop-blur-sm">
            <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                    <h2 className="text-xl font-bold flex items-center gap-3">
                        <CurrentStepIcon size={24} className="text-[var(--accent-color)]" />
                        {FORM_STEPS[currentStep - 1]?.title}
                    </h2>
                    <span className="text-sm text-gray-500">{currentStep} / {FORM_STEPS.length}</span>
                </div>
                <p className="text-sm text-gray-400">{FORM_STEPS[currentStep - 1]?.description}</p>
                <div className="w-full bg-gray-700 rounded-full h-1 mt-3">
                    <div className="bg-[var(--accent-color)] h-1 rounded-full transition-all duration-500" style={{ width: `${progress}%` }}></div>
                </div>
            </div>

            <AnimatePresence mode="wait">
                <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                >
                    {renderStepContent()}
                </motion.div>
            </AnimatePresence>

            <div className="flex justify-between mt-8 pt-6 border-t border-gray-800">
              <button onClick={prevStep} disabled={currentStep === 1} className="px-4 py-2 bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:enabled:bg-gray-600"><ChevronLeft/></button>
              <button onClick={nextStep} disabled={currentStep === FORM_STEPS.length} className="px-4 py-2 bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:enabled:bg-gray-600"><ChevronRight/></button>
            </div>
          </div>

          <div className="lg:col-span-3 flex flex-col items-center justify-center">
            <div
              ref={cardRef}
              className="w-[500px] h-[295px] p-4 rounded-xl shadow-2xl flex gap-4 relative overflow-hidden transition-all duration-300"
              style={{
                  backgroundColor: design.backgroundColor,
                  color: design.textColor,
                  fontFamily: design.font,
                  boxShadow: `0 0 25px -5px var(--glow-color), 0 0 10px -6px var(--glow-color)`,
                  border: `1px solid ${design.accentColor}40`
              }}
            >
              <div className="absolute top-0 left-0 w-1/2 h-1/2 border-t-2 border-l-2 opacity-30" style={{borderColor: design.accentColor}}></div>
              <div className="absolute bottom-0 right-0 w-1/2 h-1/2 border-b-2 border-r-2 opacity-30" style={{borderColor: design.accentColor}}></div>
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/subtle-grid.png')] opacity-10"></div>

              <div className="w-[120px] flex-shrink-0 flex flex-col justify-between items-center z-10">
                <div
                  className="w-full h-[140px] border-2 bg-black/20"
                  style={{
                    borderColor: design.accentColor,
                    boxShadow: `inset 0 0 10px var(--glow-color)`,
                    clipPath: 'polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%)'
                  }}
                >
                  {renderProfilePic()}
                </div>
                <div className="w-full h-[100px] flex items-center justify-center">
                   <QRCodeCanvas
                      value={`Agent: ${form.name}, Role: ${form.role}`}
                      size={80}
                      fgColor={design.accentColor}
                      bgColor="transparent"
                      level="Q"
                    />
                </div>
              </div>

              <div className="flex-grow flex flex-col justify-between z-10 min-w-0">
                  <div>
                    <p className="text-xs uppercase tracking-widest" style={{ color: design.accentColor }}>Agent Name</p>
                    <h3 className="text-2xl font-bold truncate" title={form.name || "Agent Name"}>{form.name || "Agent Name"}</h3>

                    <p className="text-xs uppercase tracking-widest mt-2" style={{ color: design.accentColor }}>Classification</p>
                    <p className="text-lg font-semibold truncate" title={form.role || "IT Role"}>{form.role || "IT Role"}</p>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                        <p className="text-xs uppercase tracking-widest" style={{ color: design.accentColor }}>Tech Stack</p>
                        <p className="text-sm font-mono truncate" title={form.techStack || "Tech Stack"}>{form.techStack || "Tech Stack"}</p>
                    </div>
                     <div>
                        <p className="text-xs uppercase tracking-widest" style={{ color: design.accentColor }}>Directive</p>
                        <p className="text-xs italic opacity-80 truncate" title={form.motto || "Motto"}>"{form.motto || "Motto"}"</p>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-700 pt-1">
                      <p className="font-mono text-xs opacity-60">{employeeId}</p>
                  </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
